import { Flex } from "@chakra-ui/react";
import { forwardRef } from "react";

/**
 * RadioCardGroup — visual radio buttons rendered as flat tiles that read at a
 * glance. Active tile is highlighted with the brand colour border + info-wash
 * background, matching the design system's "soft-editorial" pastel cards.
 *
 * Use for short option lists (2–6) where a real `<Select>` would be overkill
 * and a `<RadioGroup>` would be too clinical — e.g. display type, layout
 * behaviour, credential-type hint.
 *
 * Theme-aware via `--bg-primary`, `--border-default`, `--accent-info-wash`,
 * and `--text-*` tokens.
 */
export interface RadioCardOption<T extends string = string> {
  id: T;
  label: string;
  /** Optional sub-label (shown smaller and dimmer below the label). */
  sub?: string;
}

export interface RadioCardGroupProps<T extends string = string> {
  value: T;
  onChange: (next: T) => void;
  options: RadioCardOption<T>[];
  /** Grid wrap break / minimum tile width. Defaults to 130px. */
  minWidth?: number;
}

function RadioCardGroupInner<T extends string = string>(
  { value, onChange, options, minWidth = 130 }: RadioCardGroupProps<T>,
  ref: React.Ref<HTMLDivElement>,
) {
  return (
    <Flex ref={ref} gap={2} flexWrap="wrap">
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            style={{
              flex: "1 1 0",
              minWidth,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 4,
              padding: "10px 14px",
              background: active
                ? "var(--accent-info-wash)"
                : "var(--bg-primary)",
              border:
                "1px solid " +
                (active
                  ? "var(--brand-primary, #3B82F6)"
                  : "var(--border-default)"),
              borderRadius: 10,
              cursor: "pointer",
              textAlign: "left",
              color: "var(--text-primary)",
              fontFamily: "var(--font-body)",
              transition: "background-color 0.15s, border-color 0.15s",
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 500 }}>{opt.label}</span>
            {opt.sub && (
              <span
                style={{
                  fontSize: 11,
                  color: "var(--text-secondary-color)",
                }}
              >
                {opt.sub}
              </span>
            )}
          </button>
        );
      })}
    </Flex>
  );
}

export const RadioCardGroup = forwardRef(RadioCardGroupInner) as <
  T extends string = string,
>(
  props: RadioCardGroupProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => ReturnType<typeof RadioCardGroupInner>;

export default RadioCardGroup;
